import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { checkPrivileges } from "../../Helpers";
import Button from "../common/Button";
import CustomDialog from "../common/Dialog";
import MarkerMap from "../common/Maps";
import useIdentityForm from "../identity/useIdentityForm";
import { computeCreateFieldErrors } from "../identity/validation";
import AddUserModal from "./addUserModal";
import {
  assignPermission,
  countDevices,
  countUsers,
  countVehicles,
  createIdentity,
  handleIdentityError,
  listAccountManagers,
  listAccountsForPicker,
  listCompanies,
  listGarages,
  listParentUsers,
  listVehicles,
} from "./usersApi";

const CreateUpdateForm = ({
  translate,
  onCancel,
  onCreated,
  dispatch,
}) => {
  const logInUser = useSelector((state) => state.logInUsers);
  const roles = useSelector((state) => state.roles);
  const cities = useSelector((state) => state.Cities?.cities);
  const themecolors = useSelector((state) => state.themecolors);
  const parentUserLimits = useSelector((state) => state.parentUserLimits);
  const parentUnitLimits = useSelector((state) => state.parentUnitLimits);
  const identity = useIdentityForm(logInUser);
  const [lookups, setLookups] = useState({
    defaultCompaniesList: [],
    defaultAccountManagerList: [],
    defaultVehiclesList: [],
    defaultGarageOptions: [],
    defaultAccountsList: [],
    defaultUsersList: [],
    parentUsersLimit: parentUserLimits,
    parentUnitsLimit: parentUnitLimits,
    vehicleLimit: 0,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [companies, managers, vehicles, garages] = await Promise.all([
          listCompanies(),
          listAccountManagers(),
          listVehicles(logInUser.id),
          listGarages(logInUser.id),
        ]);
        let accounts = [];
        let users = [];
        if (logInUser.userType === -1) {
          accounts = await listAccountsForPicker(logInUser.accountId, logInUser);
          users = await listParentUsers(logInUser.accountId, logInUser);
        }
        const [devicesCount, usersCount, vehiclesCount] = await Promise.all([
          countDevices(logInUser.id),
          countUsers(logInUser.id),
          countVehicles(logInUser.id),
        ]);
    const updatedCompanies =
  companies?.length > 0
    ? companies.map((item) => ({
        ...item,
        label:item?.companyName||item.name
      }))
    : [];
        setLookups({
          defaultCompaniesList: updatedCompanies,
          defaultAccountManagerList: managers,
          defaultVehiclesList: vehicles,
          defaultGarageOptions: garages,
          defaultAccountsList: accounts,
          defaultUsersList: users,
          parentUsersLimit: usersCount,
          parentUnitsLimit: devicesCount,
          vehicleLimit: vehiclesCount,
        });
      } catch (error) {
        toast.error(handleIdentityError(error, translate));
      }
    };
    if (logInUser?.id) load();
  }, [logInUser, translate]);

  useEffect(() => {
    if (identity.form.contractType) {
      listAccountManagers().then((managers) =>
        setLookups((prev) => ({ ...prev, defaultAccountManagerList: managers })),
      );
    }
  }, [identity.form.contractType]);

  const searchGarageList = async (inputValue) => {
    if (!inputValue) return [];
    return listGarages(logInUser.id, inputValue);
  };

  const formSubmit = async () => {
    const errors = computeCreateFieldErrors(
      identity.form,
      identity.accountsform,
      { managerAccountSelect: identity.managerAccountSelect },
    );
    if (Object.keys(errors).length) {
      identity.setFieldErrors(errors);
      toast.error(translate("emptyFields"));
      return;
    }
    identity.setFieldErrors({});
    try {
      const created = await createIdentity(
        identity.form,
        identity.accountsform,
        {
          managerAccountSelect: identity.managerAccountSelect,
          selectedVehicle: identity.selectedVehicle,
        },
      );
      const createdUser = created?.user || created;
      // managedUserId permission call disabled
      // const createdAmUserId = getAmUserId({
      //   account: created?.account,
      //   user: createdUser,
      //   managerAccountSelect: identity.managerAccountSelect,
      //   form: identity.form,
      // });
      // if (createdUser?.id && createdAmUserId) {
      //   await assignPermission({
      //     userId: createdAmUserId,
      //     managedUserId: createdUser.id,
      //   });
      // }
      if (createdUser?.userType === 1 && identity.selectedVehicle?.id) {
        await assignPermission({
          userId: createdUser.id,
          vehicleId: identity.selectedVehicle.id,
        });
      }
      toast.success(translate("userIsCreated"));
      onCreated(created);
    } catch (error) {
      toast.error(handleIdentityError(error, translate));
    }
  };

  return (
    <>
      <AddUserModal
        {...identity}
        {...lookups}
        addOption
        roleTypes={roles?.filter((item) => item.id > 7)}
        cities={cities}
        translate={translate}
        searchGarageList={searchGarageList}
        logInUser={logInUser}
        formSubmit={formSubmit}
        modalControle={onCancel}
        buttonText={translate("sharedCreate")}
        userType={logInUser.userType}
        usersLimit={
          (lookups.parentUsersLimit?.remaningLimit || 0) -
          (lookups.parentUsersLimit?.totalCount || 0)
        }
        vehicleLimit={lookups.vehicleLimit}
        deviceLimit={
          (lookups.parentUnitsLimit?.remaningLimit || 0) -
          (lookups.parentUnitsLimit?.totalCount || 0)
        }
        hasAccessOfUpdate={checkPrivileges("userUpdate")}
        hasAccessOfDelete={checkPrivileges("userDelete")}
        hasAccessOfCreate={checkPrivileges("userCreate")}
      />
      {identity.isMapModal && (
        <CustomDialog
          title={translate("setLocation")}
          themeColors={themecolors}
          visable
          onClose={() => identity.setIsMapModal(false)}
          isButtonVisable
          draggable
          CancelText={translate("sharedBack")}
          fullWidth
          translate={translate}
          headerActions={
            <Button
              size="small"
              variant="contained"
              color="secondary"
              disabled={!identity.isVisableAddlocBtn}
              onClick={identity.setSelectedLocation}
            >
              {translate("sharedAdd")}
            </Button>
          }
          bodyPadding={0}
        >
          <MarkerMap
            getPostion={identity.getPostion}
            minHeight={500}
            zoom={identity.form.zoom || 0}
            lat={identity.form.latitude || 0}
            lng={identity.form.longitude || 0}
            height="100%"
          />
        </CustomDialog>
      )}
    </>
  );
};

export default CreateUpdateForm;
